import React, { useEffect, useState } from "react";
import { api, eur } from "@/lib/api";
import { addItem } from "@/lib/cart";
import { toast } from "sonner";
import { Sparkles, Plus } from "lucide-react";

/**
 * Fase 16 — customer-facing suggestion cards.
 * Fires view/click/conversion analytics events fire-and-forget.
 * `variant="product"` → "Perfetto con…" inside the product sheet.
 * `variant="cart"`    → "Manca qualcosa?" inside the cart drawer.
 */

const KIND_HINT = {
  recommended:  { label: "Perfetto con…",      emoji: "✨" },
  addon:        { label: "Aggiungi al piatto", emoji: "＋" },
  alternative:  { label: "Oppure prova",       emoji: "↔" },
  premium:      { label: "Upgrade premium",    emoji: "⭐" },
  wine_pairing: { label: "Consigliato con questo piatto", emoji: "🍷" },
};

function _sessionId() {
  try {
    let s = sessionStorage.getItem("sb_sugg_session");
    if (!s) {
      s = crypto.randomUUID();
      sessionStorage.setItem("sb_sugg_session", s);
    }
    return s;
  } catch { return null; }
}

function _fireEvent(suggestion_id, event_type, extra = {}) {
  const body = { suggestion_id, event_type, session_id: _sessionId(), ...extra };
  // Never block the UI on analytics.
  api.post("/public/suggestions/event", body).catch(() => { /* silent */ });
}

export default function SuggestionCards({ productId, excludeIds = [], variant = "product",
                                         onAdd }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const excl = excludeIds.filter(Boolean).join(",");
    const url = variant === "cart"
      ? `/public/suggestions/for-cart?product_ids=${encodeURIComponent(excl)}`
      : `/public/suggestions/for-product?product_id=${productId}&exclude_ids=${encodeURIComponent(excl)}`;
    api.get(url)
      .then((r) => {
        if (cancelled) return;
        const list = r.data?.suggestions || [];
        setItems(list);
        // Fire 'view' for each item shown (deduped server-side per session).
        list.forEach((it) => _fireEvent(it.suggestion_id, "view"));
      })
      .catch(() => { if (!cancelled) setItems([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [productId, variant, excludeIds.join(",")]);

  if (loading || items.length === 0) return null;

  const isCart = variant === "cart";
  const heading = isCart ? "Manca qualcosa?" : "Perfetto con…";

  const onCardClick = (it) => {
    _fireEvent(it.suggestion_id, "click");
    if (it.product?.modifier_group_ids?.length && onAdd) {
      // Let the parent handle opening the ProductSheet for products with mods.
      onAdd(it);
      return;
    }
    // Add directly, no modifiers.
    const p = it.product;
    addItem({ id: p.id, name: p.name, price_cents: p.price_cents }, 1, [], null);
    _fireEvent(it.suggestion_id, "conversion", { value_cents: p.price_cents || 0 });
    toast.success(`${p.name} aggiunto al carrello`);
  };

  return (
    <section className="rounded-2xl border border-bronze-200 bg-bronze-50/40 p-3 space-y-2"
             data-testid={isCart ? "sugg-cart-block" : "sugg-product-block"}>
      <header className="flex items-center gap-2 text-bronze-800 text-sm font-semibold">
        <Sparkles className="w-4 h-4" /> {heading}
      </header>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {items.map((it) => {
          const p = it.product;
          const hint = KIND_HINT[it.kind] || KIND_HINT.recommended;
          return (
            <button key={it.suggestion_id}
                    onClick={() => onCardClick(it)}
                    data-testid={`sugg-card-${it.target_product_id}`}
                    className="text-left rounded-xl border border-sand-300 bg-white p-2 hover:shadow-md transition group">
              {p.image_thumb_url || p.image_url ? (
                <img src={p.image_thumb_url || p.image_url} alt={p.name}
                     loading="lazy"
                     className="w-full h-20 object-cover rounded-lg" />
              ) : (
                <div className="w-full h-20 rounded-lg bg-sand-100 flex items-center justify-center text-3xl">
                  {hint.emoji}
                </div>
              )}
              <div className="mt-1.5">
                <div className="text-[10px] uppercase tracking-wider text-bronze-600">
                  {hint.emoji} {hint.label}
                </div>
                <div className="font-medium text-sm leading-tight line-clamp-2">{p.name}</div>
                <div className="mt-1 flex items-center justify-between">
                  <span className="text-xs text-ink-500">{eur(p.price_cents)}</span>
                  <span className="text-xs text-bronze-700 group-hover:underline flex items-center gap-0.5">
                    <Plus className="w-3 h-3" /> Aggiungi
                  </span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}
